import Swiper, { Navigation } from 'swiper';

import {
    getProcessItemScale,
    getProcessNavigationRoot,
    getProcessOpacityDelay,
    getProcessPathOverflow,
    getProcessPathRatio,
    getProcessSideOffset,
    getProcessStateDuration,
    isProcessSlideActive,
} from '../../utils/process-path.utils.js';

const initializedWrappers = new WeakSet();

const initProcessSwiper = (scope = document) => {
    scope.querySelectorAll('.process-swiper-wr').forEach((wrapper) => {
        if (initializedWrappers.has(wrapper)) {
            return;
        }

        const swiperEl = wrapper.querySelector('.swiper');
        const path = wrapper.querySelector('[data-process-path]');
        const slidesCount = swiperEl?.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide').length ?? 0;

        if (!swiperEl || !path || !slidesCount) {
            return;
        }

        const navigationRoot = getProcessNavigationRoot(wrapper);
        const nextEl = navigationRoot.querySelector('.swiper-button-next');
        const prevEl = navigationRoot.querySelector('.swiper-button-prev');
        let syncFrame = null;
        let transitionTimer = null;
        let stateDuration = 0;
        let isLoopFixing = false;

        const applyStateDuration = (swiper, duration) => {
            stateDuration = duration;

            swiper.slides.forEach((slide) => {
                slide.querySelector('[data-process-item]')?.style.setProperty('--process-state-duration', `${stateDuration}ms`);
            });
        };

        const syncSlides = (swiper) => {
            const wrapperBounds = wrapper.getBoundingClientRect();
            const pathBounds = path.getBoundingClientRect();
            const pathLength = path.getTotalLength();
            const sideOffset = getProcessSideOffset(window.innerWidth);

            swiper.updateSlidesOffset();

            swiper.slides.forEach((slide) => {
                const item = slide.querySelector('[data-process-item]');
                const trigger = slide.querySelector('[data-process-trigger]');
                const description = slide.querySelector('[data-process-description]');

                if (!item || !trigger || !description) {
                    return;
                }

                const progress = Number.isFinite(slide.progress) ? slide.progress : 0;
                const distance = Math.abs(progress);
                const focus = Math.max(0, 1 - Math.min(distance, 1));
                const pathProgress = -progress;
                const pathRatio = getProcessPathRatio(pathProgress, sideOffset);
                const pathOverflow = getProcessPathOverflow(pathProgress, sideOffset);
                const point = path.getPointAtLength(pathLength * pathRatio);
                const pathX = pathBounds.left - wrapperBounds.left + point.x / 1441 * pathBounds.width + pathOverflow * pathBounds.width;
                const pathY = pathBounds.top - wrapperBounds.top + point.y / 117 * pathBounds.height;
                const slideOffset = Number.isFinite(slide.swiperSlideOffset) ? slide.swiperSlideOffset : 0;
                const slideRealIndex = Number(slide.dataset.swiperSlideIndex);
                const isActive = isProcessSlideActive(slideRealIndex, swiper.realIndex, progress);
                const isVisible = distance <= 1.15;
                const wasVisible = item.dataset.processVisible === 'true';
                const isDragging = swiper.touchEventsData.isTouched && !isLoopFixing;
                const opacityDelay = getProcessOpacityDelay(isVisible, wasVisible, stateDuration, isDragging);

                item.style.setProperty('--process-x', `${pathX - slideOffset}px`);
                item.style.setProperty('--process-y', `${pathY}px`);
                item.style.setProperty('--process-focus', focus.toFixed(4));
                item.style.setProperty('--process-scale', getProcessItemScale(progress).toFixed(4));
                item.style.setProperty('--process-item-opacity', isVisible ? '1' : '0');
                item.style.setProperty('--process-opacity-delay', `${opacityDelay}ms`);
                item.dataset.processVisible = isVisible ? 'true' : 'false';
                trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
                description.setAttribute('aria-hidden', isActive ? 'false' : 'true');
                slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
                trigger.tabIndex = isVisible ? 0 : -1;
            });
        };

        const scheduleSyncSlides = (swiper) => {
            if (syncFrame !== null) {
                window.cancelAnimationFrame(syncFrame);
            }

            syncFrame = window.requestAnimationFrame(() => {
                syncFrame = null;
                syncSlides(swiper);
            });
        };

        const syncTransition = (swiper, duration) => {
            const isDragging = swiper.touchEventsData.isTouched;
            const nextStateDuration = getProcessStateDuration(duration, isDragging, isLoopFixing);

            applyStateDuration(swiper, nextStateDuration);

            swiper.slides.forEach((slide) => {
                const item = slide.querySelector('[data-process-item]');

                item?.style.setProperty('--process-duration', `${duration}ms`);
            });

            if (transitionTimer !== null) {
                window.clearTimeout(transitionTimer);
                transitionTimer = null;
            }

            if (duration > 0) {
                transitionTimer = window.setTimeout(() => {
                    transitionTimer = null;

                    if (!swiper.destroyed && swiper.animating) {
                        swiper.transitionEnd();
                    }

                    scheduleSyncSlides(swiper);
                }, duration);
            }
        };

        const swiper = new Swiper(swiperEl, {
            modules: [Navigation],
            observer: true,
            observeParents: true,
            slidesPerView: 1,
            centeredSlides: true,
            loop: slidesCount > 1,
            loopedSlides: slidesCount,
            loopAdditionalSlides: 0,
            loopedSlidesLimit: true,
            initialSlide: Math.min(1, slidesCount - 1),
            speed: 700,
            threshold: 10,
            longSwipesRatio: 0.18,
            virtualTranslate: true,
            watchSlidesProgress: true,
            navigation: nextEl && prevEl ? {
                nextEl,
                prevEl,
            } : undefined,
            on: {
                init: syncSlides,
                beforeLoopFix(swiper) {
                    isLoopFixing = true;
                    syncTransition(swiper, 0);
                },
                loopFix(swiper) {
                    syncTransition(swiper, 0);
                    syncSlides(swiper);
                    isLoopFixing = false;

                    if (swiper.touchEventsData.isTouched) {
                        applyStateDuration(swiper, getProcessStateDuration(0, true, false));
                    }
                },
                setTranslate(swiper) {
                    if (swiper.touchEventsData.isTouched && !isLoopFixing) {
                        applyStateDuration(swiper, getProcessStateDuration(0, true, false));
                    }

                    syncSlides(swiper);
                },
                setTransition: syncTransition,
                touchStart(swiper) {
                    applyStateDuration(swiper, getProcessStateDuration(0, true, false));
                },
                activeIndexChange: scheduleSyncSlides,
                slideChange: scheduleSyncSlides,
                resize: syncSlides,
                transitionEnd: scheduleSyncSlides,
            },
        });

        wrapper.addEventListener('click', (event) => {
            const trigger = event.target.closest('[data-process-trigger]');

            if (!trigger || !wrapper.contains(trigger)) {
                return;
            }

            const slide = trigger.closest('.swiper-slide');
            const realIndex = Number(slide?.dataset.swiperSlideIndex);

            if (Number.isInteger(realIndex) && realIndex !== swiper.realIndex) {
                swiper.slideToLoop(realIndex);
                scheduleSyncSlides(swiper);
            }
        });

        initializedWrappers.add(wrapper);
        syncSlides(swiper);
    });
};

initProcessSwiper();
window.initProcessSwiper = initProcessSwiper;
